import {
  useEffect,
  useState,
  useRef,
} from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Popup,
  useMap,
} from "react-leaflet";
import {
  PlusOutlined,
  MinusOutlined,
  AimOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import L from "leaflet";
import markerSvg from "../../assets/markers/substation.png";
import completedMarkerSvg from "../../assets/markers/substation-completed.png";
import mahaZonesUrl from "../../assets/geojson/MAHA_VITARAN_1.geojson";
import "../../utils/leafletIcon";
import "leaflet/dist/leaflet.css";
import DistrictClusterMarkers from "../../utils/DistrictClusterMarkers";
import "./MapCanvas.css";
import ProjectDetailsPanel from "./ProjectDetailsPanel"

// Custom current location icon
const currentLocationIcon = new L.DivIcon({
  html: `
    <div class="current-location-marker">
      <div class="pulse-ring-current"></div>
      <div class="location-dot"></div>
    </div>
  `,
  className: "",
  iconSize: [40, 40],
  iconAnchor: [20, 20],
});

function MapTools({ onLayerChange, selectedLayer, onCurrentLocation, panelOpen, }) {
  const map = useMap();
  const [showLayers, setShowLayers] = useState(false);

  const handleZoomIn = () => map.zoomIn();
  const handleZoomOut = () => map.zoomOut();

  const handleCurrentLocation = () => {
    onCurrentLocation?.();
  };

  return (
    <>
      <div className="map-tools">
        <button className="tool-btn" onClick={handleCurrentLocation}>
          <AimOutlined />
        </button>

        <button className="tool-btn" onClick={handleZoomIn}>
          <PlusOutlined />
        </button>

        <button className="tool-btn" onClick={handleZoomOut}>
          <MinusOutlined />
        </button>
      </div>
    </>
  );
}

function FlyToProject({ selectedProject, markerRefs }) {
  const map = useMap();

  useEffect(() => {
    if (!selectedProject) return;

    const lat = Number(selectedProject.latitude);
    const lng = Number(selectedProject.longitude);

    map.flyTo([lat, lng], 11, { duration: 1.5 });

    setTimeout(() => {
      markerRefs.current[selectedProject.id]?.openPopup();
    }, 1200);
  }, [selectedProject, map, markerRefs]);

  return null;
}

function FitProjectsBounds({ projects }) {
  const map = useMap();

  useEffect(() => {
    if (!projects || projects.length === 0) return;

    const bounds = projects.map((project) => [
      Number(project.latitude),
      Number(project.longitude),
    ]);

    if (projects.length === 1) {
      map.flyTo(bounds[0], 11, { duration: 1.5 });
    } else {
      map.fitBounds(bounds, { padding: [80, 80] });
    }
  }, [projects, map]);

  return null;
}

function MapCanvas({ projects, loading, selectedProject }) {
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [geojsonData, setGeojsonData] = useState(null);
  const markerRefs = useRef({});
  const [selectedLayer, setSelectedLayer] = useState("light");
  const [currentLocation, setCurrentLocation] = useState(null);
  const currentMarkerRef = useRef(null);

  useEffect(() => {
    fetch(mahaZonesUrl)
      .then((res) => res.json())
      .then((data) => setGeojsonData(data)) // already WGS84 — no conversion needed
      .catch((err) => console.error(err));
  }, []);

  const projectIcon = new L.DivIcon({
    html: `
      <div class="custom-marker">
        <span class="pulse-ring"></span>
        <img src="${markerSvg}" alt="marker" />
      </div>
    `,
    className: "",
    iconSize: [60, 60],
    iconAnchor: [30, 60],
  });
  const completedProjectIcon = new L.DivIcon({
    html: `
      <div class="custom-marker completed-marker">
        <span class="pulse-ring completed-ring"></span>
        <img src="${completedMarkerSvg}" alt="completed marker" />
      </div>
    `,
    className: "",
    iconSize: [60, 60],
    iconAnchor: [30, 60],
  });
  const zoneColors = {
    "Amravati Zone": "#7ED957",
    "Chhatrapati Sambhajinagar Zone": "#FF8FA3",
    "Karad Zone": "#F5E642",
    "Nagpur Zone": "#FFB84D",
    "Nashik Zone": "#69D2E7",
    "Pune Zone": "#7EA6FF",
    "Vashi Zone": "#E76BF3",
  };

  const getZoneStyle = (feature) => {
    if (feature.properties.Zone === "State") {
      return {
        color: "#a8a8a8",
        weight: 1,
        opacity: 0.8,
        fillOpacity: 0, // outline only, no fill — lets the plain no-labels tile show through
      };
    }

    return {
      color: "#6e6e6e",
      weight: 1,
      opacity: 1,
      fillColor: zoneColors[feature.properties.Zone] || "#dcdcdc",
      fillOpacity: 0.75,
    };
  };

  const onEachFeature = (feature, layer) => {
    const district = feature.properties.district;
    const zone = feature.properties.Zone;
    const stateName = feature.properties.state;

    if (zone === "State") {
      // neighboring state: ONE label with just the state name, nothing else
      layer.bindTooltip(
        `<div class="district-zone-label">
          <div class="district-name">${stateName}</div>
        </div>`,
        { permanent: true, direction: "center", className: "custom-tooltip state-label" }
      );
      return;
    }

    if (!district) return;

    layer.bindTooltip(
      `<div class="district-zone-label">
        <div class="district-name">${district}</div>
      </div>`,
      { permanent: true, direction: "center", className: "custom-tooltip" }
    );

    layer.on({
      mouseover: (e) => {
        e.target.setStyle({ weight: 2.5, color: "#da241c", fillOpacity: 0.35 });
      },
      mouseout: (e) => {
        e.target.setStyle(getZoneStyle(feature));
      },
    });
  };

  const handleLayerChange = (layer) => {
    setSelectedLayer(layer);
  };

  const handleCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        if (lat >= 15.4 && lat <= 22.8 && lng >= 72.5 && lng <= 80.9) {
          setCurrentLocation({ lat, lng });

          const map = currentMarkerRef.current?._map;
          if (map) {
            map.flyTo([lat, lng], 12, { duration: 1.2 });
          }
        } else {
          alert("Current location is outside Maharashtra region.");
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        alert("Unable to get current location. Please enable location access.");
      }
    );
  };

  const tileLayers = {
    street: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    light: "https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png", // no baked-in text
    satellite: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  };

  return (
    <div className="map-canvas">
      <div
        style={{
          width: selectedMarker ? "calc(100% - 420px)" : "100%",
          height: "100%",
          transition: "0.3s",
        }}
      >
        <MapContainer
          center={[19.4, 76]}
          zoom={7}
          minZoom={7}
          maxZoom={12}
          zoomControl={false}
          style={{ height: "100%", width: "100%" }}
          maxBounds={[[15.4, 72.5], [22.8, 80.9]]}
          maxBoundsViscosity={1}
          preferCanvas={true}
        >
          <TileLayer url={tileLayers[selectedLayer]} />

          {geojsonData && (
            <GeoJSON
              data={geojsonData}
              style={getZoneStyle}
              onEachFeature={onEachFeature}
            />
          )}

          {/* {!loading &&
            projects?.map((project) => (
              <Marker
                icon={
                    project.status === "COMPLETED"
                      ? completedProjectIcon
                      : projectIcon
                  }
                key={project.id}
                ref={(ref) => {
                  if (ref) markerRefs.current[project.id] = ref;
                }}
                position={[
                  Number(project.latitude),
                  Number(project.longitude),
                ]}
                eventHandlers={{
                  click: () => {
                    setSelectedMarker(project);
                  },
                }}
              />
            ))} */}
          {!loading && (
            <DistrictClusterMarkers
              projects={projects}
              projectIcon={projectIcon}
              completedProjectIcon={completedProjectIcon}
              markerRefs={markerRefs}
              onMarkerClick={(project) => setSelectedMarker(project)}
            />
          )}
          {currentLocation && (
            <Marker
              position={[currentLocation.lat, currentLocation.lng]}
              icon={currentLocationIcon}
              ref={currentMarkerRef}
            >
              <Popup>
                <div>
                  <h3>Your Current Location</h3>
                  <p>Latitude: {currentLocation.lat.toFixed(6)}</p>
                  <p>Longitude: {currentLocation.lng.toFixed(6)}</p>
                </div>
              </Popup>
            </Marker>
          )}

          <FlyToProject selectedProject={selectedProject} markerRefs={markerRefs} />
          <FitProjectsBounds projects={projects} />

          <MapTools
            selectedLayer={selectedLayer}
            onLayerChange={handleLayerChange}
            onCurrentLocation={handleCurrentLocation}
            panelOpen={!!selectedMarker}
          />
        </MapContainer>
      </div>
     <div className="horizontal-zone-legend">


      {/* Project Status */}
      <div className="legend-section">
        <div className="legend-subtitle">Project Status</div>

        <div className="legend-item">
          <img
            src={markerSvg}
            alt="Active Project"
            className="legend-marker-icon"
          />
          <span>Active / In Progress</span>
        </div>

        <div className="legend-item">
          <img
            src={completedMarkerSvg}
            alt="Completed Project"
            className="legend-marker-icon"
          />
          <span>Completed</span>
        </div>
      </div>

      <div className="legend-divider" />

      {/* Zones */}
      <div className="legend-subtitle">Zones</div>

      {Object.entries(zoneColors).map(([zone, color]) => (
        <div key={zone} className="legend-item">
          <span
            className="legend-color"
            style={{ backgroundColor: color }}
          />
          <span>{zone}</span>
        </div>
      ))}
    </div>
      <ProjectDetailsPanel
        project={selectedMarker}
        onClose={() => setSelectedMarker(null)}
      />
    </div>
  );
}

export default MapCanvas;